// Shared motion for the simulator. Every animated element spreads `motion()`
// (or adds REDUCED itself) so players with prefers-reduced-motion get the
// final state instantly.
import { useEffect, useRef, useState } from 'react';
import { keyframes } from '@mui/system';

export const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

export const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

export const popIn = keyframes`
  0% { opacity: 0; transform: scale(0.85); }
  60% { opacity: 1; transform: scale(1.04); }
  100% { opacity: 1; transform: scale(1); }
`;

export const bubbleIn = keyframes`
  from { opacity: 0; transform: translateY(8px) scale(0.96); }
  to { opacity: 1; transform: translateY(0) scale(1); }
`;

export const slideDown = keyframes`
  from { opacity: 0; transform: translateY(-14px); }
  to { opacity: 1; transform: translateY(0); }
`;

export const shake = keyframes`
  0%, 100% { transform: translateX(0); }
  20% { transform: translateX(-6px); }
  40% { transform: translateX(5px); }
  60% { transform: translateX(-4px); }
  80% { transform: translateX(2px); }
`;

export const floatUp = keyframes`
  0% { opacity: 0; transform: translateY(4px); }
  15% { opacity: 1; transform: translateY(0); }
  75% { opacity: 1; transform: translateY(-4px); }
  100% { opacity: 0; transform: translateY(-10px); }
`;

export const typingDot = keyframes`
  0%, 60%, 100% { transform: translateY(0); opacity: 0.45; }
  30% { transform: translateY(-4px); opacity: 1; }
`;

export const pulseRing = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(121, 201, 18, 0.45); }
  100% { box-shadow: 0 0 0 8px rgba(121, 201, 18, 0); }
`;

export const glow = keyframes`
  0% { filter: brightness(1); }
  40% { filter: brightness(1.25); }
  100% { filter: brightness(1); }
`;

export const ring = keyframes`
  0%, 100% { transform: rotate(0); }
  15% { transform: rotate(14deg); }
  30% { transform: rotate(-12deg); }
  45% { transform: rotate(8deg); }
  60% { transform: rotate(-4deg); }
`;

export const REDUCED = '@media (prefers-reduced-motion: reduce)';

// `animation` plus the reduced-motion opt-out, ready to spread into a style.
export const motion = (animation) => ({
  animation,
  [REDUCED]: { animation: 'none' }
});

export const prefersReducedMotion = () => {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch (e) {
    return false;
  }
};

// Animates a number toward `value` (ease-out). Jumps straight there when the
// player prefers reduced motion or the page is hidden (rAF is paused then).
export const useCountUp = (value, duration = 700, from = null) => {
  const [shown, setShown] = useState(from ?? value);
  const shownRef = useRef(shown);
  shownRef.current = shown;
  useEffect(() => {
    const start = shownRef.current;
    if (start === value) return undefined;
    if (prefersReducedMotion() || document.hidden) {
      setShown(value);
      return undefined;
    }
    let raf;
    const t0 = performance.now();
    const tick = (now) => {
      const p = Math.min(1, (now - t0) / duration);
      const eased = 1 - (1 - p) ** 3;
      setShown(start + (value - start) * eased);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value, duration]);
  return shown;
};

export const ripple = keyframes`
  0% { transform: scale(1); opacity: 0.55; }
  100% { transform: scale(1.9); opacity: 0; }
`;

export const wave = keyframes`
  0%, 100% { transform: scaleY(0.3); }
  50% { transform: scaleY(1); }
`;

export const blink = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0; }
`;
